import { Component, OnInit, AfterViewInit, EventEmitter, Output, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { jwtDecode } from 'jwt-decode';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

declare const google: any;

/**
 * The JSON structure we expect from `users.json`.
 */
interface UsersFile {
  adminUsers?: string[];
  internalUsers: string[];
  pilotUsers: string[];
}

@Component({
  selector: 'app-login-gmail',
  standalone: true,
  templateUrl: './login-gmail.component.html',
  imports: [CommonModule, HttpClientModule],
  styleUrls: ['./login-gmail.component.css']
})
export class LoginGmailComponent implements OnInit, AfterViewInit {
  @Output() userLoggedIn = new EventEmitter<string | null>();
  @Input() buttonIdSuffix: string = '';
  user: any = null; // Decoded user info from Google
  isAppRoute = false; // Track if we're in the /app route

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService
  ) {
    // Subscribe to router events to check the current URL
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.isAppRoute = event.url === '/app' || event.url.startsWith('/app/');
      this.cdr.detectChanges();
    });
  }

  ngOnInit(): void {
    // Check initial route
    this.isAppRoute = this.router.url === '/app' || this.router.url.startsWith('/app/');
    this.loadUserFromLocalStorage();
  }

  ngAfterViewInit(): void {
    console.log('🔧 Initializing Google Sign-in component');
    this.loadGoogleApi().then(() => {
      console.log('✅ Google API loaded successfully');
      this.initializeGoogleSignIn();
    }).catch(err => {
      console.error('❌ Failed to load Google API:', err);
    });
  }

  /**
   * Initialize Google Sign-In with OpenPYQ configuration
   */
  initializeGoogleSignIn(): void {
    console.log('🔑 Initializing Google Sign-In with OpenPYQ Client ID:', environment.googleClientId);
    
    if (typeof google === 'undefined') {
      console.error('❌ Google API not loaded.');
      return;
    }

    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: any) => this.handleCredentialResponse(response),
      auto_select: false,
      cancel_on_tap_outside: true
    });

    setTimeout(() => {
      const buttonContainer = document.getElementById('google-signin-button' + this.buttonIdSuffix);
      if (!buttonContainer) {
        console.error('❌ Google Sign-In button container not found!');
        return;
      }
      
      console.log('🎨 Rendering Google Sign-In button');
      google.accounts.id.renderButton(
        buttonContainer,
        {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          logo_alignment: 'left',
          width: 250
        }
      );
    }, 500);
  }

  /**
   * Handle the response from Google Identity Services (Frontend JWT Decoding + Backend Storage)
   */
  handleCredentialResponse(response: any): void {
    console.log('🔐 Google Auth Response received');
    console.log('🔍 Token details:', {
      hasCredential: !!response.credential,
      tokenLength: response.credential ? response.credential.length : 0
    });
    
    try {
      // Decode the credential (JWT) directly in frontend like mindmapsforupsc
      const decoded: any = jwtDecode(response.credential);
      console.log('✅ Decoded User:', decoded);

      // Show full name if length < 16, otherwise show only first name
      let displayName = decoded.name;
      if (displayName.length >= 16) {
        displayName = decoded.name.split(' ')[0];
      }

      // Store the user object in local storage immediately for UI responsiveness
      localStorage.setItem('googleUser', JSON.stringify(decoded));

      // Determine user role from local JSON first
      this.determineUserRole(decoded.email)
        .then(role => {
          console.log('✅ User role determined:', role);
          
          // Store user data in backend database
          this.storeUserInBackend(decoded, role)
            .then(() => {
              // After successful backend storage, complete the login process
              this.completeLoginProcess(decoded, role, displayName);
            })
            .catch(backendError => {
              console.warn('⚠️ Backend storage failed, continuing with frontend-only login:', backendError);
              // Continue with frontend-only login even if backend fails
              this.completeLoginProcess(decoded, role, displayName);
            });
        })
        .catch(roleError => {
          console.error('⚠️ Error determining user role:', roleError);
          // Fallback to general role
          const fallbackRole = 'general';
          
          this.storeUserInBackend(decoded, fallbackRole)
            .then(() => {
              this.completeLoginProcess(decoded, fallbackRole, displayName);
            })
            .catch(backendError => {
              console.warn('⚠️ Backend storage failed, continuing with frontend-only login:', backendError);
              this.completeLoginProcess(decoded, fallbackRole, displayName);
            });
        });

    } catch (error) {
      console.error('❌ Error decoding JWT:', error);
      alert('Authentication failed: Invalid token format');
    }
  }

  /**
   * Store user data in backend database
   */
  private storeUserInBackend(googleData: any, userRole: string): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log('💾 Storing user data in backend...');
      
      this.http.post(`${environment.apiBaseUrl}/auth/store-user`, {
        googleData: googleData,
        userRole: userRole
      }).subscribe({
        next: (response: any) => {
          console.log('✅ User data stored in backend:', response);
          resolve(response);
        },
        error: (error) => {
          console.error('❌ Backend storage failed:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Complete the login process after user data is processed
   */
  private completeLoginProcess(decoded: any, role: string, displayName: string): void {
    // Store final user data
    this.user = decoded;
    localStorage.setItem('userRole', role);

    // Update auth service
    this.authService.setUserInfo(decoded, role);
    console.log('✅ Auth service notified about login');

    // Emit event to parent component
    this.userLoggedIn.emit(displayName);
    console.log('✅ Parent component notified about login with name:', displayName);

    // Force change detection
    this.cdr.detectChanges();

    // Navigate based on role
    if (role === 'admin') {
      // Admin users go directly to admin route
      console.log('🔐 Admin user detected, redirecting to /admin');
      this.router.navigate(['/admin']);
    } else if (!this.isAppRoute) {
      // Other users go to app if not already there
      this.router.navigate(['/app']);
    }

    console.log('🎉 Login process completed successfully!');
  }

  /**
   * Determine the user role (admin, internal, pilot, or general) from a single JSON file
   */
  determineUserRole(email: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Normalize the email for matching
      const normalizedEmail = email.toLowerCase().trim();

      // Fetch the single users.json
      this.http.get<UsersFile>('assets/users.json').subscribe({
        next: (data: UsersFile) => {
          console.log('📋 Loaded users.json:', data);

          // Check if user is in adminUsers first
          if (data.adminUsers) {
            const isAdmin = data.adminUsers
              .map(u => u.toLowerCase().trim())
              .includes(normalizedEmail);
            if (isAdmin) {
              console.log('🔐 User is ADMIN');
              return resolve('admin');
            }
          }

          // Check if user is in internalUsers
          const inInternal = data.internalUsers
            .map(u => u.toLowerCase().trim())
            .includes(normalizedEmail);
          if (inInternal) {
            return resolve('internal');
          }

          // Else check if user is in pilotUsers
          const inPilot = data.pilotUsers
            .map(u => u.toLowerCase().trim())
            .includes(normalizedEmail);
          if (inPilot) {
            return resolve('pilot');
          }

          // Otherwise default to 'general'
          resolve('general');
        },
        error: (err) => {
          console.warn('⚠️ Could not load users.json, defaulting to general role:', err);
          resolve('general');
        }
      });
    });
  }

  /**
   * Load user from local storage if already logged in
   */
  loadUserFromLocalStorage(): void {
    const storedUser = localStorage.getItem('googleUser');
    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser);

        // Show full name if length < 16, otherwise show only first name
        let displayName = this.user.name;
        if (displayName && displayName.length >= 16) {
          displayName = this.user.name.split(' ')[0];
        }

        this.userLoggedIn.emit(displayName);
        console.log('👤 User loaded from localStorage:', {
          email: this.user.email,
          name: this.user.name
        });
        
        this.cdr.detectChanges();
      } catch (error) {
        console.error('❌ Error parsing stored user data:', error);
        // Clear invalid data
        this.clearStoredAuthData();
      }
    }
  }

  /**
   * Logout logic: clear local storage, user object, re-render Google button
   */
  logout(): void {
    console.log('🚪 Logging out user...');
    
    // Call auth service logout
    this.authService.logout();
    
    // Clear local component state
    this.user = null;
    this.userLoggedIn.emit(null);
    
    console.log('🧹 Logout completed, reinitializing Google Sign-in');
    this.cdr.detectChanges();

    // Reinitialize Google Sign-in button after a short delay
    setTimeout(() => {
      this.initializeGoogleSignIn();
    }, 100);
  }

  /**
   * Clear all stored authentication data
   */
  private clearStoredAuthData(): void {
    localStorage.removeItem('googleUser');
    localStorage.removeItem('userRole');
    localStorage.removeItem('sessionToken'); // Clean up old backend tokens
    localStorage.removeItem('userType'); // Legacy cleanup
  }

  /**
   * Load Google API script
   */
  loadGoogleApi(): Promise<void> {
    console.log('📡 Loading Google API...');
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined') {
        console.log('✅ Google API already loaded');
        resolve();
      } else {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log('✅ Google API script loaded');
          resolve();
        };
        script.onerror = () => {
          console.error('❌ Google API script failed to load');
          reject('Google API failed to load.');
        };
        document.head.appendChild(script);
      }
    });
  }

  /**
   * Check if user is currently logged in
   */
  isLoggedIn(): boolean {
    return !!(this.user && localStorage.getItem('googleUser'));
  }

  /**
   * Get current user display name
   */
  getUserDisplayName(): string {
    if (!this.user) return '';
    
    let displayName = this.user.name || this.user.email || 'User';
    if (displayName.length >= 16) {
      displayName = displayName.split(' ')[0];
    }
    return displayName;
  }
}
